import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import pdfImage from "../../Components/Assets/images/pdf.jfif"; // Check paths
import excelImage from "../../Components/Assets/images/excel.png";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import LoaderBackdrop from "../../Components/Loader/LoaderBackdrop";
import DownloadCustomerLedgerReportPdf from "../../Components/Download/DownloadCustomerLedgerReportPdf";
import DownloadCustomerLedgerReportExcel from "../../Components/Download/DownloadCustomerLedgerReportExcel";
import ItemLedgerPopup from "../../Components/Inventory/ItemLedgerPopup";
import VisibilityIcon from "@mui/icons-material/Visibility";

const CustomerItemReport = () => {
  // State management
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { company_id, companyName } = useSelector(
    (state) => state.GlobalVariables,
  );

  // Mock API Call - Replace with your actual configServ call
  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Yahan aapka actual API call aayega
      const result = await configServ.customerReport({
        company_id,
        search,
        page: page + 1,
        per_page: rowsPerPage,
      });

      if (result.status) {
        setReport(result.data);
        setTotalCount(result.total);
      } else {
        setReport([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    setPage(0); // ✅ reset page
  }, [search]);

  const [companyLogo, setCompanyLogo] = useState("");
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const assignCompanyLogo = async () => {
    try {
      const result = await configServ.company_logo({ company_id: company_id });
      if (result && result.image) {
        // Assuming the result.image is already a base64 string
        setCompanyLogo(`data:image/png;base64,${result.image}`);
      } else {
        setCompanyLogo(null); // Set as null if no logo
      }
    } catch (e) {
      console.log(e);
      setCompanyLogo(null); // Handle error by clearing logo
    }
  };

  useEffect(() => {
    assignCompanyLogo();
  }, []);

  const handleDownloadPdf = () => {
    DownloadCustomerLedgerReportPdf(
      report,
      companyName,
      companyLogo,
      setOpenBackdrop,
    );
  };

  const handleDownloadExcel = () => {
    DownloadCustomerLedgerReportExcel(report, companyName, setOpenBackdrop);
  };

  const [openItemLedger, setOpenItemLedger] = useState(false);
  const [itemLedger, setItemLedger] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemLedgerLoading, setItemLedgerLoading] = useState(false);
    const [itemList, setItemList] = useState([]);
  
const fetchItemList = async (searchByItem = null, ItemName = null) => {
    try {
      setItemLedgerLoading(true);
      const dataToSend = {
        company_id: company_id,
        // search_by: searchByItem ? searchByItem : searchBy,
        // search: ItemName ? ItemName : debounceSearch,
        // per_page: rowsPerPage,
        // page: page,
        // sort_by: orderBy,
        // order,
      };
      const res = await configServ.getStockList(dataToSend);
      if (res.status === 200) {
        const result = res.data;
        if (result.length > 0) {
          setItemList(result);
          // setCount(res.total);
        } else {
          setItemList([]);
          // setCount(0);
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setItemLedgerLoading(false);
    }
  };
  const handleItemLedger = async (params) => {
    try {
      await fetchItemList();
      setItemLedgerLoading(true);

      setOpenItemLedger(true);
      const item = itemList.find((x) => x.id === params);
      const dataToSend = {
        item_id: params,
        //per_page: 10,
        //page: 0
      };
      const res = await configServ.getItemLedger(dataToSend);
      if (res.status === 200) {
        setItemLedger(res.data);
        setSelectedItem(item);
      } else {
        setItemLedger([]);
        setSelectedItem(null);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setItemLedgerLoading(false);
    }
  };
   const handleItemLedgerClose = () => {
    setOpenItemLedger(false);
    setItemLedger([]);
    setSelectedItem(null);
    setItemLedgerLoading(false);
  };

  return (
    <>
      <ItemLedgerPopup
        openItemLedger={openItemLedger}
        handleItemLedgerClose={handleItemLedgerClose}
        itemLedger={itemLedger}
        selectedItem={selectedItem}
        itemLedgerLoading={itemLedgerLoading}
      />
      <Box
        sx={{
          p: 2,
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <LoaderBackdrop open={itemLedgerLoading} />
        {/* Header & Search Section */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="primary">
              Site Stock Report
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search Customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "gray", mr: 1 }} />,
                }}
                sx={{ width: 300 }}
              />
              <Tooltip title="Download PDF" onClick={handleDownloadPdf}>
                <IconButton>
                  <img src={pdfImage} width={25} alt="pdf" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Excel" onClick={handleDownloadExcel}>
                <IconButton>
                  <img src={excelImage} width={25} alt="excel" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* Content Section */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress />
          </Box>
        ) : report.length > 0 ? (
          <>
            {report.map((customer) => (
              <Accordion
                key={customer.customer_id}
                sx={{ mb: 2, border: "1px solid #e0e0e0", boxShadow: "none" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                      pr: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                      >
                        {customer.customer_name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <LocationOnIcon sx={{ fontSize: 16, color: "gray" }} />
                        <Typography variant="body2" color="textSecondary">
                          {customer.customer_address}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={`${customer.items.length} Items`}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#f0f7ff" }}>
                        <TableRow>
                          <TableCell>
                            <b>Item Name</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Unit</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Location</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Opening</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>InWard Qty</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>OutWard Qty</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Total Qty</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customer.items.map((item) => (
                          <TableRow key={item.item_id} hover>
                            <TableCell sx={{ fontWeight: 500 }}>
                              <VisibilityIcon
                                sx={{
                                  borderRadius: 10,
                                  width: "fit-content",
                                  transition: "100ms",
                                  cursor: "pointer",
                                  padding: 0,
                                  m: 0.5,
                                  ":hover": { backgroundColor: "#99999955" },
                                }}
                                color="primary"
                                onClick={() => handleItemLedger(item.item_id)}
                              />{" "}
                              {item.item_name}
                            </TableCell>
                            <TableCell align="center">
                              {item.item_unit}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.item_location}
                                size="small"
                                sx={{ fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {item.opening_qty}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "blue" }}>
                              {item.challan_qty}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "orange" }}>
                              {item.credit_qty}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  color:
                                    item.total_qty < 0
                                      ? "error.main"
                                      : "success.main",
                                }}
                              >
                                {item.total_qty}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        ) : (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <Typography color="textSecondary">
              No customer data found matching your search.
            </Typography>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default CustomerItemReport;
